import { DateTime } from "luxon";

export const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy") : "-";

export const renderAnswer = (q: any) => {
  const ans = q?.answer;

  if (ans == null || ans === "") return "-";
  if (Array.isArray(ans)) return ans.length ? ans.join(", ") : "-";

  if (typeof ans === "string" && /^\d{4}-\d{2}-\d{2}/.test(ans)) {
    return formatDate(ans);
  }

  return String(ans);
};

/**
 * Display value for a single answer on the Profile Forms tab, aware of the
 * question field type. Returns "" for an empty answer so the caller can decide
 * how to render it (e.g. "Not answered"). Multi-select answers arrive as an
 * array; dates as an ISO string.
 */
export const formatAnswerValue = (answer: any, fieldType?: any): string => {
  const raw = answer?.answer;

  if (raw == null || raw === "") return "";
  if (Array.isArray(raw)) return raw.length ? raw.join(", ") : "";

  const slug = fieldType?.slug_name;
  if (slug === "date" || (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw))) {
    return formatDate(raw);
  }

  return String(raw);
};

/** The Forms endpoint formats created_on as "DD-MM-YYYY HH:MM:SS". */
const FORMS_TIMESTAMP_FORMAT = "dd-MM-yyyy HH:mm:ss";

/** Millis for sorting answers by capture time; 0 when unparseable/absent. */
export const parseFormsTimestamp = (value?: string | null): number => {
  if (!value) return 0;
  const dt = DateTime.fromFormat(value, FORMS_TIMESTAMP_FORMAT);
  return dt.isValid ? dt.toMillis() : 0;
};

/** Human-readable capture time, e.g. "17/06/2026 08:29". */
export const formatFormsTimestamp = (value?: string | null): string => {
  if (!value) return "-";
  const dt = DateTime.fromFormat(value, FORMS_TIMESTAMP_FORMAT);
  return dt.isValid ? dt.toFormat("dd/MM/yyyy HH:mm") : value;
};



/**
 * Upload timestamp for a document row.
 *
 * The two document endpoints hand back `created_on` in different shapes, and
 * the two areas parsed them differently: the Profile tab patched the space to
 * a "T" and used `fromISO`, the side panel used `fromSQL` and rendered the
 * literal string "Invalid DateTime" to the user when it failed. This accepts
 * either shape, formats both the same way, and falls back to an em-dash.
 */
export const formatDocumentTimestamp = (value?: string | null): string => {
  if (!value) return "—";

  const parsed = [
    DateTime.fromISO(value.replace(" ", "T")),
    DateTime.fromSQL(value),
  ].find((dt) => dt.isValid);

  return parsed ? parsed.toLocal().toFormat("dd/MM/yyyy HH:mm") : "—";
};

export const getFormattedDob = (dob) => {
  if (!dob) return "-";

  // Case 1: dob is an array
  if (Array.isArray(dob) && dob.length > 0) {
    const date = DateTime.fromISO(dob[0]);
    return date.isValid ? date.toFormat("dd/MM/yyyy") : "-";
  }

  // Case 2: dob is an object (or string)
  if (typeof dob === "object" && dob.date) {
    const date = DateTime.fromISO(dob.date);
    return date.isValid ? date.toFormat("dd/MM/yyyy") : "-";
  }

  // Case 3: dob is a string
  if (typeof dob === "string") {
    const date = DateTime.fromISO(dob);
    return date.isValid ? date.toFormat("dd/MM/yyyy") : "-";
  }

  return "-";
};

